import { useDeployStatus } from '../lib/useDeployStatus';
import { Icon } from '../lib/icons';

/**
 * Release-in-progress banner. The deploy script (deploy/remote-deploy.sh) writes
 * a short-lived flag to Redis just before rolling services and clears it when the
 * deploy finishes; this polls GET /api/deploy-status and shows a warning while the
 * flag is set — so users learn the service may be briefly unstable, with no reload.
 *
 * Polling (not SSE) is deliberate: the API container itself restarts mid-deploy, so
 * a stream would drop at exactly the wrong moment. React Query retains the last value
 * across failed polls, so the banner HOLDS through the brief API outage and only hides
 * once the new API reports `inProgress: false` (or the Redis TTL clears the flag).
 *
 * Non-dismissible by design — it auto-hides when the deploy completes.
 */
export function ReleaseBanner() {
  const { status: data } = useDeployStatus();

  if (!data?.inProgress) return null;

  // Full-bleed second row of the sticky topbar — the chrome (bg/border/padding/
  // colors/font) lives in .app-topbar-release; the parent .app-topbar is the
  // sticky element (this row stays a plain block so it can wrap freely).
  return (
    <div className="app-topbar-release" role="status" aria-live="polite">
      <Icon name="rocket" size={15} style={{ flexShrink: 0, marginTop: 1, color: 'var(--accent-warning)' }} />
      <span style={{ minWidth: 0 }}>
        A new version is being deployed{data.version ? ` (${data.version})` : ''} — the service may be
        briefly unstable. This message clears automatically when the update finishes.
      </span>
    </div>
  );
}
